<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePermissions, type ModuleName } from '@/composables/usePermissions'

const router = useRouter()
const authStore = useAuthStore()
const { canView, canCreate, canAdmin } = usePermissions()

// Sidebar toujours collapsed, étendue uniquement au survol
const isHovered = ref(false)
const isExpanded = computed(() => isHovered.value)

// Vérifier si l'utilisateur est dev (super admin ou niveau dev >= 1)
const isDev = computed(() => {
  const roles = authStore.user?.roles
  if (!roles) return false
  // Super admin a toujours accès (sauf si simulation active)
  if (roles.is_super_admin && !roles.simulation_active) return true
  // Sinon vérifier le niveau dev
  return (roles.dev ?? 0) >= 1
})

// Vérifier si l'utilisateur est super admin (sans simulation)
const isSuperAdmin = computed(() => {
  const roles = authStore.user?.roles
  if (!roles) return false
  return roles.is_super_admin && !roles.simulation_active
})

// Icônes SVG pour chaque section (paths pour Heroicons outline)
const menuIconPaths: Record<string, string> = {
  'Dashboard': 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  'Mon Dashboard': 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z',
  'Ressources Humaines': 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  'Projets': 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
  'Société': 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  'Facturation': 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  'Support': 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  'Documents': 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  'Ordre de mission': 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8',
  'Dev / Tests': 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
  'Administrateur': 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  'Déconnexion': 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
}

interface MenuItem {
  name: string
  path: string
  children?: MenuItem[]
  external?: boolean
  viewModule?: ModuleName   // Module requis pour voir (niveau >= 1)
  createModule?: ModuleName // Module requis pour créer (niveau >= 3)
  adminModule?: ModuleName  // Module requis pour admin (niveau >= 4 / GLOBAL)
  devOnly?: boolean         // Visible uniquement pour les devs
  superAdminOnly?: boolean  // Visible uniquement pour les super admins
  omValidationOnly?: boolean // Visible uniquement pour Cellule Lead / RH / PDG / admin
}

const menuItems: MenuItem[] = [
  { name: 'Dashboard', path: '/' },
  {
    name: 'Mon Dashboard',
    path: '/my-dashboard',
    devOnly: true,
    children: [
      { name: 'Mes pages', path: '/my-dashboard', devOnly: true },
    /*   { name: 'Creer une page', path: '/my-dashboard/creer' }, */
    ]
  },
  {
    name: 'Ressources Humaines',
    path: '/rh',
    viewModule: 'hr',
    children: [
      { name: 'Créer un utilisateur', path: '/rh/utilisateurs/creer', createModule: 'hr' }, // Niveau 3+
      { name: 'Utilisateurs', path: '/rh/utilisateurs' },
      { name: 'Messages', path: '/rh/messages', createModule: 'hr' }, // Niveau 3+
    ]
  },
  {
    name: 'Projets',
    path: '/projets',
    viewModule: 'project',
    children: [
      { name: 'Créer', path: '/projets/opportunites/creer', createModule: 'project' },
      { name: 'Liste des projets', path: '/projets/liste' },
      { name: 'Suivi projet', path: '/projets/suivi', createModule: 'project' }, // Niveau 3+
    ]
  },
  {
    name: 'Société',
    path: '/societes',
    viewModule: 'company',
    children: [
      { name: 'Créer une société', path: 'https://capacites.my.youday.app/', external: true, createModule: 'company' },
      { name: 'Sociétés', path: '/societes/liste' },
      { name: 'Contacts', path: '/societes/contacts' },
    ]
  },
  {
    name: 'Facturation',
    path: '/facturation',
    viewModule: 'finance',
    children: [
      { name: 'Suivi Facturation', path: '/facturation/suivi' },
      { name: 'Archives', path: '/facturation/archives' }, // Perso / Cellule : leurs propres projets
      { name: 'Échéanciers', path: '/facturation/echeanciers' }, // Niveau 2+
    ]
  },
  {
    name: 'Support',
    path: '/support',
    viewModule: 'support',
    children: [
      { name: 'Encours', path: '/support/encours', createModule: 'project' }, // Niveau 3+
      { name: 'Gérer les projets', path: '/support/gerer-projets' },
      { name: 'Paramètres des devis', path: '/support/parametres-devis' },
      { name: 'Liste des matériels', path: '/support/materiels' },
      { name: 'Liste des laboratoires', path: '/support/laboratoires' },
      { name: 'Liste des cellules', path: '/support/cellules' },
      { name: 'Liste des thèmes', path: '/support/themes' },
      { name: 'Règles de notification', path: '/support/regles-notification' },
      { name: 'Historique des projets', path: '/support/historique' },
    ]
  },
  { name: 'Documents', path: '/docs', viewModule: 'wiki' },
  {
    name: 'Ordre de mission',
    path: '/om',
    viewModule: 'mission_order',
    children: [
      { name: 'Créer un OM', path: '/om/creer', viewModule: 'mission_order' }, // Niveau 1+ peut créer
      { name: 'Liste des OM', path: '/om/liste' },
      { name: 'Validation', path: '/om/validation', omValidationOnly: true }, // Cellule Lead / RH / PDG / admin
      { name: 'Archives', path: '/om/archives', createModule: 'mission_order' }, // Niveau 3+ (Cell Lead)
    ]
  },
  // Dev / Tests : accessible uniquement aux devs (super admin ou niveau dev >= 1)
  {
    name: 'Dev / Tests',
    path: '/dev',
    devOnly: true,
    children: [
      { name: 'Composants', path: '/dev/components', devOnly: true },
      { name: 'Lots', path: '/dev/lots-test', devOnly: true },
      { name: 'Charts', path: '/dev/charts-test', devOnly: true },
      { name: 'PDF Viewer', path: '/dev/pdf-viewer', devOnly: true },
      { name: 'Calendrier', path: '/dev/calendar', devOnly: true },
      { name: 'Mail', path: '/dev/mail-test', devOnly: true },
      { name: 'Milestones', path: '/dev/milestones', devOnly: true },
      { name: 'Laboxy', path: '/dev/laboxy', devOnly: true },
      { name: 'Laboxy Times', path: '/dev/laboxy-times', devOnly: true },
      { name: 'Laboxy Tasks', path: '/dev/laboxy-tasks', devOnly: true },
      { name: 'Laboxy Projects', path: '/dev/laboxy-projects-list', devOnly: true },
      { name: 'Laboxy TimeManagement', path: '/dev/laboxy-timemanagement', devOnly: true },
      { name: 'Conditions devis (aperçu)', path: '/dev/devis-terms-preview', devOnly: true },
    ]
  },
  // Administrateur : accessible uniquement aux super admins (accès base de données)
  {
    name: 'Administrateur',
    path: '/admin',
    superAdminOnly: true,
    children: [
      { name: 'Projet', path: '/admin/projet', superAdminOnly: true },
      { name: 'Synchronisation', path: '/admin/sync', superAdminOnly: true },
      { name: 'Widgets', path: '/admin/widgets', superAdminOnly: true },
      { name: 'Statistiques', path: '/admin/statistiques', superAdminOnly: true },
      { name: 'Liens rapides', path: '/admin/liens-rapides', superAdminOnly: true },
      { name: 'Notifications', path: '/admin/notifications', superAdminOnly: true },
    ]
  },
]

// Vérifie si un item de menu est visible selon les permissions
const isMenuItemVisible = (item: MenuItem): boolean => {
  // Si superAdminOnly, visible uniquement pour les super admins
  if (item.superAdminOnly) {
    return isSuperAdmin.value
  }
  // Si devOnly, visible uniquement pour les devs
  if (item.devOnly) {
    return isDev.value
  }
  // Validation OMs : scope mission_order >= 3 (Cellule Lead) / RH / PDG / super admin
  if (item.omValidationOnly) {
    const roles = authStore.user?.roles
    if (!roles) return false
    if (roles.is_super_admin && !roles.simulation_active) return true
    if (roles.is_pdg) return true
    if (roles.is_rh) return true
    if ((roles.mission_order ?? 0) >= 3) return true
    return !!roles.lead_cellule_ids?.length
  }
  // Si adminModule est défini, il faut canAdmin (niveau GLOBAL / 4)
  if (item.adminModule) {
    return canAdmin(item.adminModule)
  }
  // Si createModule est défini, il faut canCreate
  if (item.createModule) {
    return canCreate(item.createModule)
  }
  // Si viewModule est défini, il faut canView
  if (item.viewModule) {
    return canView(item.viewModule)
  }
  // Sinon visible par défaut
  return true
}

// Menu filtré par permissions
const filteredMenuItems = computed(() => {
  return menuItems
    .filter(item => isMenuItemVisible(item))
    .map(item => {
      if (item.children) {
        return {
          ...item,
          children: item.children.filter(child => isMenuItemVisible(child))
        }
      }
      return item
    })
    // Supprimer les groupes vides
    .filter(item => !item.children || item.children.length > 0)
})

const expandedMenus = ref<string[]>([])

const toggleMenu = (name: string) => {
  const index = expandedMenus.value.indexOf(name)
  if (index > -1) {
    expandedMenus.value.splice(index, 1)
  } else {
    expandedMenus.value.push(name)
  }
}

const logout = async () => {
  await authStore.logout()
  router.push('/login')
}

const openExternal = (url: string) => {
  window.open(url, '_blank')
}

// Obtenir le path SVG pour un item
const getIconPath = (name: string): string => {
  return menuIconPaths[name] || ''
}
</script>

<template>
  <aside
    class="sidebar"
    :class="{ 'sidebar--collapsed': !isHovered, 'sidebar--expanded': isExpanded }"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <!-- Logo -->
    <div class="sidebar-header">
      <router-link to="/" class="sidebar-logo">
        <img src="@/assets/logo-icon.png" alt="Capatools" class="logo-icon" />
        <span v-if="isExpanded" class="logo-text">Capatools</span>
      </router-link>
    </div>

    <!-- Menu -->
    <nav class="sidebar-nav">
      <template v-for="item in filteredMenuItems" :key="item.path">
        <!-- Item avec enfants -->
        <div v-if="item.children" class="nav-group">
          <button
            class="nav-item nav-parent"
            @click="toggleMenu(item.name)"
            :title="!isExpanded ? item.name : undefined"
          >
            <span class="nav-icon">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getIconPath(item.name)" />
              </svg>
            </span>
            <span v-if="isExpanded" class="nav-label">{{ item.name }}</span>
            <span v-if="isExpanded" class="nav-arrow">
              <svg class="w-3 h-3 transition-transform" :class="{ 'rotate-90': expandedMenus.includes(item.name) }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </button>
          <div
            v-if="expandedMenus.includes(item.name) && isExpanded"
            class="nav-children"
          >
            <template v-for="child in item.children" :key="child.path">
              <!-- Lien externe -->
              <button
                v-if="child.external"
                class="nav-item nav-child nav-external"
                @click="openExternal(child.path)"
              >
                <span class="nav-label">{{ child.name }}</span>
                <svg class="w-3 h-3 ml-1 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
              <!-- Lien interne -->
              <router-link
                v-else
                :to="child.path"
                class="nav-item nav-child"
              >
                <span class="nav-label">{{ child.name }}</span>
              </router-link>
            </template>
          </div>
        </div>

        <!-- Item simple -->
        <router-link
          v-else
          :to="item.path"
          class="nav-item"
          :title="!isExpanded ? item.name : undefined"
        >
          <span class="nav-icon">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getIconPath(item.name)" />
            </svg>
          </span>
          <span v-if="isExpanded" class="nav-label">{{ item.name }}</span>
        </router-link>
      </template>
    </nav>

    <!-- Footer -->
    <div class="sidebar-footer">
      <button
        class="nav-item logout-btn"
        @click="logout"
        :title="!isExpanded ? 'Déconnexion' : undefined"
      >
        <span class="nav-icon">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getIconPath('Déconnexion')" />
          </svg>
        </span>
        <span v-if="isExpanded" class="nav-label">Déconnexion</span>
      </button>
    </div>
  </aside>
</template>

<style scoped>
.sidebar {
  @apply fixed left-0 top-0 h-screen bg-dark text-white flex flex-col z-50;
  width: 260px;
  transition: width 0.2s ease;
}

.sidebar--collapsed {
  width: 60px;
}

.sidebar--collapsed:hover,
.sidebar--expanded {
  width: 260px;
}

/* Quand collapsed et hovered, ajouter une ombre pour montrer le overlay */
.sidebar--collapsed:hover {
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.3);
}

.sidebar-header {
  @apply flex items-center p-4 border-b border-dark-lighter;
  min-height: 60px;
}

.sidebar-logo {
  @apply flex items-center gap-2 text-white hover:text-primary-light;
}

.logo-icon {
  @apply w-8 h-8 flex-shrink-0 rounded-lg;
}

.logo-text {
  @apply text-xl font-bold whitespace-nowrap;
}

.sidebar-nav {
  @apply flex-1 py-4 overflow-y-auto overflow-x-hidden;
}

.nav-group {
  @apply mb-1;
}

.nav-item {
  @apply flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-dark-lighter hover:text-white transition-colors cursor-pointer w-full;
}

.nav-item.router-link-active {
  @apply bg-primary/20 text-primary-light border-r-4 border-primary;
}

.nav-icon {
  @apply w-6 h-6 flex items-center justify-center flex-shrink-0 text-base;
}

.nav-parent {
  @apply justify-between;
}

.nav-label {
  @apply flex-1 text-left whitespace-nowrap overflow-hidden;
}

.nav-arrow {
  @apply text-gray-500 flex-shrink-0;
}

.nav-arrow svg {
  @apply transition-transform duration-200;
}

.nav-arrow .rotate-90 {
  transform: rotate(90deg);
}

.nav-children {
  @apply bg-dark-darker;
}

.nav-child {
  @apply pl-12;
}

.sidebar-footer {
  @apply border-t border-dark-lighter p-2;
}

.logout-btn {
  @apply text-red-400 hover:text-red-300 hover:bg-red-500/10;
}
</style>
